<!-- ./components/CardDetailComponent.vue -->
<template>
  <div class="card display-card" :style="cardStyle">
    <div class="row h-100">
      <!-- Left: cost & stats -->
      <div class="col-7mm d-flex flex-column">
        <div class="card-body no-space flex-grow-1">
          <div class="card transparent-card">
            <div class="d-flex flex-wrap justify-content-center align-items-center">
              <div v-for="(sym, i) in costSymbols" :key="i" class="symbol-container">
                <!-- If numeric cost is greater than 10, render custom grey circle with number -->
                <template v-if="isNumeric(sym) && parseInt(sym, 10) > 10">
                  <div class="custom-number-circle">
                    {{ sym }}
                  </div>
                </template>
                <!-- Otherwise, if it's a numeric or 'X' cost, use the number cost image -->
                <template v-else-if="isNumberCost(sym)">
                  <img
                    :src="getNumberCostImagePath(sym)"
                    :alt="sym"
                    class="cost-image"
                  />
                </template>
                <!-- For element symbols, use the cost image -->
                <template v-else>
                  <img
                    :src="getCostImagePath(sym)"
                    :alt="sym"
                    class="cost-image"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
        <div
          class="card transparent-card no-space text-center d-flex flex-column align-items-center"
          v-if="card.attack !== null"
        >
          <h5 class="combat-stat" style="margin-top:1mm">
            {{ card.attack }}
          </h5>
          <hr class="stat-divider" />
          <h5 class="combat-stat">
            {{ card.health }}
          </h5>
          <hr class="stat-divider" />
          <h5 class="combat-stat" style="margin-bottom:1mm">
            {{ card.speed }}
          </h5>
        </div>
      </div>

      <!-- Right: identity & body -->
      <div class="col">
        <div class="card-body no-space">
          <div class="card transparent-card card-title-container justify-content-center">
            <h5 class="card-title">{{ card.name }}</h5>
          </div>
          <div class="text-outline card-type-text">
            {{ card.displayCardType }}
          </div>
          <!-- Art section uses artName directly if it has extension -->
          <div class="card-image">
            <img
              v-if="cardImageExists"
              :src="cardImagePath"
              alt="Card Art"
            />
            <div class="card-image-credit" v-if="cardImageExists">
              <span>Illustrator: Jason Glover</span>
            </div>
          </div>
          <div
            class="card-rules transparent-card p-2"
            :style="ruleStyle"
            v-html="rulesHtml"
          ></div>
          <div class="card-provides">
            <div v-for="(sym, i) in colorIdentity" :key="i">
              <img
                :src="getProvideImagePath(sym)"
                :alt="sym"
                class="cost-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from "vue";
import {
  GetCardSymbols,
  GetCardColorIdentity,
  GetCardBodyHtml,
} from "../services/CardAdapterService";

export default {
  name: "CardDetailComponent",
  props: {
    card: { type: Object, required: true },
  },
  setup(props) {
    // Map for bracketed element symbols (case-insensitive)
    const elementMap = { A: "Abyssal", R: "Arcane", L: "Light", E: "Terra", X: "Neutral" };

    // Determine the correct filename (artName may already include “.png”)
    const artFileName = computed(() => {
      const raw = props.card.artName || props.card.name;
      return raw.toLowerCase().endsWith(".png") ? raw : `${raw}.png`;
    });

    const cardImagePath = ref(`/art/${artFileName.value}`);
    const cardImageExists = ref(false);

    const checkImageExists = (path) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth > 0);
        img.onerror = () => resolve(false);
        img.src = path;
      });

    const costSymbols = ref([]);
    const colorIdentity = ref([]);
    const rulesHtml = ref("");

    const recompute = async () => {
      costSymbols.value = await GetCardSymbols(props.card);
      colorIdentity.value = GetCardColorIdentity(costSymbols.value);
      // Get raw body HTML and replace bracketed element symbols case-insensitive
      let body = await GetCardBodyHtml(props.card);
      body = body.replace(/\[([ARLEX])\]/gi, (match, raw) => {
        const name = elementMap[raw.toUpperCase()] || "Neutral";
        return `<img src="/symbol/Symbol${name}.png" alt="${raw}" class="rules-cost-image" />`;
      });
      rulesHtml.value = body;
    };

    onMounted(async () => {
      cardImagePath.value = `/art/${artFileName.value}`;
      cardImageExists.value = await checkImageExists(cardImagePath.value);
      await recompute();
    });

    watch(
      () => [props.card, artFileName.value],
      async () => {
        cardImagePath.value = `/art/${artFileName.value}`;
        cardImageExists.value = await checkImageExists(cardImagePath.value);
        await recompute();
      },
      { deep: true }
    );

    const cardStyle = computed(() => {
      const map = { A: "#45818e", R: "#bd94a8", L: "#f1c232", E: "#f9cb9c", X: "#cccccc" };
      const ids = colorIdentity.value.length ? colorIdentity.value : ["X"];
      const style = { width: "63mm", height: "86mm" };
      if (ids.length === 1) {
        style.backgroundColor = map[ids[0]];
      } else {
        style.backgroundImage = `linear-gradient(to right, ${ids.map((c) => map[c]).join(", ")})`;
      }
      if (props.card.isEthereal) {
        const overlay =
          "linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0) 50%)";
        style.backgroundImage = style.backgroundImage
          ? `${style.backgroundImage}, ${overlay}`
          : overlay;
      }
      return style;
    });

    const ruleStyle = computed(() => ({
      fontSize: props.card.cardSize ? `${props.card.cardSize}mm` : "2.3mm",
    }));

    // Determines if a symbol is a numeric cost (integers) for custom rendering
    const isNumeric = (p) => !isNaN(parseInt(p, 10));

    const isNumberCost = (p) => !isNaN(parseInt(p, 10)) || p === "X";
    const getNumberCostImagePath = (p) =>
      p === "X"
        ? `/symbol/SymbolX.png`
        : `/symbol/Symbol${parseInt(p, 10)}.png`;
    const getCostImagePath = (p) => {
      const map = { A: "Abyssal", R: "Arcane", L: "Light", E: "Terra", X: "Neutral" };
      const key = p.toUpperCase();
      return map[key]
        ? `/symbol/Symbol${map[key]}.png`
        : `/symbol/SymbolNeutral.png`;
    };
    const getProvideImagePath = (p) =>
      p === "X" ? `/symbol/SymbolNeutral.png` : getCostImagePath(p);

    return {
      cardImagePath,
      cardImageExists,
      costSymbols,
      colorIdentity,
      rulesHtml,
      cardStyle,
      ruleStyle,
      isNumberCost,
      isNumeric,
      getNumberCostImagePath,
      getCostImagePath,
      getProvideImagePath,
    };
  },
};
</script>

<style scoped>
.card-image {
  position: relative;
  overflow: hidden;
  /* ensure your container has the right aspect ratio */
  /* e.g. height: 45mm; width is controlled by the parent layout */
}

.card-image img {
  width: 53mm;
  height: 100%;
  object-fit: cover;
}

/* New credit strip */
.card-image-credit {
  position: absolute;
  top: 0;
  right: 0;
  width: 2mm;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-image-credit span {
  display: block;
  transform: rotate(-90deg);
  transform-origin: center;
  font-size: 1.8mm;
  white-space: nowrap;
}

/* Custom circle for numeric costs > 10 */
.custom-number-circle {
  width: 5mm;
  height: 5mm;
  border-radius: 50%;
  background-color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3mm;
  color: white;
  margin: 2px;
}
</style>
